import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Stack,
  Fade,
  Slide,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Link,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  WhatsApp as WhatsAppIcon,
  Spa as SpaIcon,
  Groups as GroupsIcon,
  CelebrationOutlined as CelebrationIcon,
  LocalFlorist as FloristIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import CitabilitySection from "../components/CitabilitySection";

const WORKSHOP_CATALOG_PDF =
  "https://res.cloudinary.com/dis3t1ajp/raw/upload/v1776062058/xwx0uafuxexnqk42pan5.pdf";

const faqs = [
  {
    q: "如何預訂企業或社區工作坊？",
    a: "請透過 WhatsApp (+852 8437 8839) 或聯絡表格聯繫我們，說明活動日期、人數及場地。我們會提供免費諮詢及客製化報價。",
  },
  {
    q: "工作坊適合多少人參加？",
    a: "我們可承接 8 至 50 人以內的活動，設有小班精緻體驗及大型活動方案，將根據參加者人數安排導師。",
  },
  {
    q: "工作坊在哪裡舉行？",
    a: "我們提供上門服務，可到貴機構的辦公室、社區中心或活動場地舉辦。",
  },
  {
    q: "有哪些手工藝工作坊類型？",
    a: "主要包括：手工蠟燭、苔玉、組合盆栽、環保手作等，詳情可參閱工作坊目錄。活動可根據主題或季節度身訂造。",
    catalogLink: true,
  },
  {
    q: "活動費用如何計算？",
    a: "費用根據活動類型、人數及場地而定。歡迎聯絡我們索取報價，批量預訂可享折扣優惠。",
  },
  {
    q: "可以為活動加上品牌元素嗎？",
    a: "可以。我們可在包裝、標籤或成品上加入公司 Logo 或訂製設計，適合品牌推廣、客戶送禮及員工紀念品。",
  },
];

const occasions = [
  {
    icon: <BusinessIcon />,
    title: "企業 Team Building",
    desc: "提升團隊凝聚力的手作療癒活動，適合任何規模",
  },
  {
    icon: <CelebrationIcon />,
    title: "公司週年慶及節日",
    desc: "聖誕節、農曆新年等節慶主題工作坊，送禮自用兩相宜",
  },
  {
    icon: <SpaIcon />,
    title: "員工身心健康日",
    desc: "減壓療癒的手工藝體驗，關注員工精神健康",
  },
  {
    icon: <GroupsIcon />,
    title: "社區中心活動",
    desc: "適合各年齡層的社區手工藝班，親子活動尤為受歡迎",
  },
  {
    icon: <FloristIcon />,
    title: "NGO 及慈善活動",
    desc: "為非牟利機構提供有意義的手作體驗，增強社區聯繫",
  },
  {
    icon: <SpaIcon />,
    title: "私人派對及生日會",
    desc: "成人生日、閨蜜手作派對等個人化活動方案",
  },
];

const highlights = [
  "上門服務，零場地準備壓力",
  "全套材料及工具由導師攜帶",
  "10 至 50+ 人均可承接",
  "可加入品牌元素及客製包裝",
  "全程由導師帶領，無需任何手作經驗",
  "超過 20 間機構合作，口碑有保證",
];

const CommunityProgram: React.FC = () => {
  const structuredData = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "企業及社區手工藝工作坊方案",
        description:
          "CosyLab 為企業、社區中心及 NGO 提供手工藝工作坊，包括蠟燭製作、植物護理等。適合 Team Building、員工活動、社區節日及私人派對。全港上門服務。",
        provider: {
          "@type": "LocalBusiness",
          name: "CosyLab",
          url: "https://cosylabdesigns.com",
        },
        areaServed: { "@type": "City", name: "Hong Kong" },
        audience: {
          "@type": "Audience",
          audienceType: "企業、社區中心、NGO、成人團體",
        },
        serviceType: [
          "企業 Team Building",
          "社區工作坊",
          "手工藝活動",
          "成人興趣班",
        ],
        url: "https://cosylabdesigns.com/community-program",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首頁",
            item: "https://cosylabdesigns.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "企業及社區方案",
            item: "https://cosylabdesigns.com/community-program",
          },
        ],
      },
    ],
    [],
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(ellipse at top right, rgba(104,159,56,0.08) 0%, transparent 50%), linear-gradient(180deg, rgba(250,251,248,1) 0%, rgba(245,248,242,1) 100%)",
      }}
    >
      <SEOHead
        title="企業 Team Building 及社區手工藝工作坊｜CosyLab 香港"
        description="CosyLab 為企業及社區中心提供手工藝工作坊：蠟燭製作、植物護理。適合 Team Building、員工活動、社區節日及私人派對。全港上門，免費諮詢。"
        canonical="https://cosylabdesigns.com/community-program"
        keywords="企業 team building 手工藝, 社區工作坊香港, 員工活動, 手工藝工作坊, 成人興趣班香港, 蠟燭製作工作坊, 企業活動香港"
        structuredData={structuredData}
      />

      {/* Hero */}
      <Box
        component="section"
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 10 },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Container maxWidth="lg">
          <Slide direction="down" in timeout={700}>
            <Box textAlign="center">
              <Chip
                icon={<GroupsIcon />}
                label="企業及社區活動專頁"
                sx={{
                  mb: 3,
                  backgroundColor: "rgba(104,159,56,0.12)",
                  color: "#2D5A27",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  border: "1px solid rgba(104,159,56,0.3)",
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "2rem", md: "3rem" },
                  lineHeight: 1.2,
                  background:
                    "linear-gradient(135deg, #1B4332 0%, #689F38 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 3,
                }}
              >
                企業及社區手工藝工作坊方案
              </Typography>
              <Typography
                variant="h2"
                component="p"
                sx={{
                  fontSize: { xs: "1.1rem", md: "1.4rem" },
                  color: "#4a6741",
                  fontWeight: 400,
                  maxWidth: 680,
                  mx: "auto",
                  lineHeight: 1.7,
                  mb: 5,
                }}
              >
                為企業 Team Building、社區活動及私人派對提供療癒手作體驗——
                蠟燭製作、植物護理，全港上門服務，零壓力策劃。
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  href="https://wa.me/85284378839"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    background:
                      "linear-gradient(135deg, #1B4332 0%, #689F38 100%)",
                    color: "white",
                    fontWeight: 700,
                    px: 4,
                    py: 1.8,
                    borderRadius: 3,
                    fontSize: "1rem",
                    boxShadow: "0 4px 20px rgba(27,67,50,0.25)",
                    "&:hover": { boxShadow: "0 6px 28px rgba(27,67,50,0.35)" },
                  }}
                >
                  WhatsApp 免費諮詢
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={RouterLink}
                  to="/classes"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    borderColor: "#689F38",
                    color: "#2D5A27",
                    fontWeight: 600,
                    px: 4,
                    py: 1.8,
                    borderRadius: 3,
                    fontSize: "1rem",
                    "&:hover": { backgroundColor: "rgba(104,159,56,0.06)" },
                  }}
                >
                  瀏覽工作坊課程
                </Button>
              </Stack>
            </Box>
          </Slide>
        </Container>
      </Box>

      {/* Occasions */}
      <Box
        component="section"
        sx={{ py: { xs: 6, md: 8 }, backgroundColor: "rgba(104,159,56,0.05)" }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={900}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                textAlign="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "#2D5A27",
                  mb: 5,
                }}
              >
                適合哪些場合？
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 3,
                }}
              >
                {occasions.map((o, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 3,
                      boxShadow: "0 2px 12px rgba(104,159,56,0.08)",
                      border: "1px solid rgba(104,159,56,0.15)",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 8px 24px rgba(104,159,56,0.18)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #1B4332 0%, #689F38 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        mb: 2,
                      }}
                    >
                      {o.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 700, color: "#2D5A27", mb: 1 }}
                    >
                      {o.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", lineHeight: 1.6 }}
                    >
                      {o.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Highlights */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                textAlign="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "#2D5A27",
                  mb: 5,
                }}
              >
                為什麼選擇 CosyLab？
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 3,
                }}
              >
                {highlights.map((h, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: 3,
                      backgroundColor: "rgba(104,159,56,0.04)",
                      borderRadius: 3,
                      border: "1px solid rgba(104,159,56,0.15)",
                    }}
                  >
                    <CheckCircleIcon
                      sx={{ color: "#689F38", mt: 0.25, flexShrink: 0 }}
                    />
                    <Typography
                      sx={{ color: "#333", fontWeight: 500, lineHeight: 1.6 }}
                    >
                      {h}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* FAQ */}
      <Box
        component="section"
        sx={{ py: { xs: 6, md: 8 }, backgroundColor: "rgba(104,159,56,0.04)" }}
      >
        <Container maxWidth="md">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                textAlign="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "#2D5A27",
                  mb: 5,
                }}
              >
                常見問題
              </Typography>
              {faqs.map((faq, i) => (
                <Accordion
                  key={i}
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border: "1px solid rgba(104,159,56,0.2)",
                    borderRadius: "12px !important",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": {
                      boxShadow: "0 4px 16px rgba(104,159,56,0.12)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#689F38" }} />}
                    sx={{ px: 3, py: 1.5 }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#2D5A27",
                        fontSize: "0.95rem",
                      }}
                    >
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                    <Typography
                      component="div"
                      sx={{ color: "#555", lineHeight: 1.8 }}
                    >
                      {faq.catalogLink ? (
                        <>
                          主要包括：手工蠟燭、苔玉、組合盆栽、環保手作等，詳情可參閱
                          <Link
                            href={WORKSHOP_CATALOG_PDF}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="always"
                            sx={{
                              color: "#689F38",
                              fontWeight: 600,
                              mx: 0.5,
                              cursor: "pointer",
                              "&:hover": { color: "#2D5A27" },
                            }}
                          >
                            工作坊目錄
                          </Link>
                          。活動可根據主題或季節度身訂造。
                        </>
                      ) : (
                        faq.a
                      )}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #1B4332 0%, #689F38 100%)",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Fade in timeout={1000}>
            <Box>
              <GroupsIcon
                sx={{ fontSize: 56, color: "rgba(255,255,255,0.8)", mb: 2 }}
              />
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: "white",
                  fontSize: { xs: "1.6rem", md: "2.2rem" },
                  mb: 2,
                }}
              >
                立即查詢企業及社區工作坊方案
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  mb: 5,
                  fontSize: "1.1rem",
                  maxWidth: 520,
                  mx: "auto",
                }}
              >
                免費諮詢，客製化方案，歡迎透過 WhatsApp 或聯絡表格與我們聯繫
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  href="https://wa.me/85284378839"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: "white",
                    color: "#1B4332",
                    fontWeight: 700,
                    px: 4,
                    py: 1.8,
                    borderRadius: 3,
                    fontSize: "1rem",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
                  }}
                >
                  WhatsApp：8437 8839
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={RouterLink}
                  to="/contact"
                  sx={{
                    borderColor: "rgba(255,255,255,0.7)",
                    color: "white",
                    fontWeight: 600,
                    px: 4,
                    py: 1.8,
                    borderRadius: 3,
                    fontSize: "1rem",
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  填寫聯絡表格
                </Button>
              </Stack>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 4 }} />
              <Typography
                sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}
              >
                是學校老師或教育機構？{" "}
                <Box
                  component={RouterLink}
                  to="/school-program"
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  查看學校到校課程方案 →
                </Box>
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default CommunityProgram;
